/**
 * FP3-032 — Supporting Documents tab.
 *
 * Lives at /students/{id}?tab=documents. Three blocks:
 *   1. Header w/ filters (type / freshness / visibility) + upload button.
 *   2. Document list (cards) with freshness pill, type, tags, attribution.
 *   3. Upload modal with title, type, date, visibility, tags, summary.
 *
 * Privacy:
 *   - Never displays storage paths.
 *   - Downloads go through authenticated streaming endpoint.
 *   - admin_only is only postable + visible to admins.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText,
  UploadSimple,
  PencilSimple,
  TrashSimple,
  Archive,
  Download,
  Eye,
  X,
  Tag,
  Calendar,
  Warning,
  FilePlus,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import DocumentViewerModal from "@/components/student/DocumentViewerModal";

const DOCUMENT_TYPES = [
  { value: "evaluation_report", label: "Evaluation report" },
  { value: "speech_language_report", label: "Speech / Language report" },
  { value: "academic_assessment", label: "Academic assessment" },
  { value: "teacher_input", label: "Teacher input" },
  { value: "parent_input", label: "Parent input" },
  { value: "work_sample", label: "Work sample" },
  { value: "progress_summary", label: "Progress summary" },
  { value: "intervention_data", label: "Intervention data" },
  { value: "outside_report", label: "Outside report" },
  { value: "meeting_notes", label: "Meeting notes" },
  { value: "classroom_observation", label: "Classroom observation" },
  {
    value: "medical_developmental_history",
    label: "Medical / developmental history",
  },
  { value: "other", label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(
  DOCUMENT_TYPES.map((t) => [t.value, t.label])
);

const FRESHNESS_LABEL = {
  current_evidence: "Current evidence",
  still_useful: "Still useful",
  older_context: "Older context",
  background_only: "Background only",
  date_not_provided: "Date not provided",
};
const FRESHNESS_TONE = {
  current_evidence: "bg-[#E8F0EB] text-[#1E3F33] border-[#C8DBC7]",
  still_useful: "bg-[#F0EDE3] text-[#6B5A2C] border-[#DDD7C6]",
  older_context: "bg-[#FAF1DD] text-[#7A5B1F] border-[#E9D9A9]",
  background_only: "bg-[#F8E8E2] text-[#7A3A2C] border-[#E8C7BC]",
  date_not_provided: "bg-[#F1F2F4] text-[#6B7280] border-[#E5E3DB]",
};

const FRESHNESS_OPTIONS = [
  { value: "", label: "Any freshness" },
  { value: "current_evidence", label: "Current evidence" },
  { value: "still_useful", label: "Still useful" },
  { value: "older_context", label: "Older context" },
  { value: "background_only", label: "Background only" },
  { value: "date_not_provided", label: "Date not provided" },
];

// FP3-034B — `DOCUMENT_TYPES` re-exported so the Assessments tab can
// limit its upload picker to assessment-relevant types and label them
// consistently with the Documents tab.
export const DOCUMENT_TYPES_EXPORT = DOCUMENT_TYPES;
export const TYPE_LABEL_EXPORT = TYPE_LABEL;
export const FRESHNESS_LABEL_EXPORT = FRESHNESS_LABEL;
export const FRESHNESS_TONE_EXPORT = FRESHNESS_TONE;

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function StudentDocumentsTab({ studentId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [freshnessFilter, setFreshnessFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  // FP3-032D — In-app viewer state. Holds the currently-previewed
  // document; null means the viewer is closed.
  const [viewingDoc, setViewingDoc] = useState(null);

  // FP3-032C — Refetch the documents list. Accepts `{ background }` so
  // post-upload refreshes don't flash the "Loading documents…"
  // placeholder over the just-uploaded row (the optimistic append
  // already shows it).
  const load = async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError("");
    // eslint-disable-next-line no-console
    console.info("[DocumentsUpload] documents list refetch starting", {
      studentId,
      typeFilter,
      freshnessFilter,
      background,
    });
    try {
      const params = {
        // FP3-034B correction — Documents tab scope ⇒ general docs
        // only. Assessment artifacts live in the Assessments tab.
        evidence_scope: "general",
      };
      if (typeFilter) params.document_type = typeFilter;
      if (freshnessFilter) params.freshness = freshnessFilter;
      const res = await api.listStudentDocuments(studentId, params);
      setDocs(res.data?.items || []);
      setViewerIsAdmin(!!res.data?.viewer_is_admin);
      // eslint-disable-next-line no-console
      console.info("[DocumentsUpload] documents list refetch complete", {
        count: (res.data?.items || []).length,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("You don't have access to this student's documents.");
      } else {
        setError(
          err?.response?.data?.detail || "Could not load documents"
        );
      }
      // eslint-disable-next-line no-console
      console.error("[DocumentsUpload] documents list refetch failed", {
        status,
        detail: err?.response?.data?.detail,
      });
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, typeFilter, freshnessFilter]);

  if (error) {
    return (
      <Card
        className="border-[#E5E3DB] rounded-2xl"
        data-testid="documents-error"
      >
        <CardContent className="p-6 text-center text-[13px] text-[#7A3A2C]">
          <Warning size={16} weight="duotone" className="inline mr-1 mb-0.5" />
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <Card className="border-[#E5E3DB] rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
              <FileText
                size={14}
                weight="duotone"
                className="text-[#3F4756]"
              />
              Documents
              <span className="ml-1.5 text-[11px] font-medium text-[#6B7280]">
                · {docs.length}
              </span>
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid="documents-filter-type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="text-[11.5px] border border-[#E5E3DB] rounded-md px-2 py-1 bg-white"
              >
                <option value="">All types</option>
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                data-testid="documents-filter-freshness"
                value={freshnessFilter}
                onChange={(e) => setFreshnessFilter(e.target.value)}
                className="text-[11.5px] border border-[#E5E3DB] rounded-md px-2 py-1 bg-white"
              >
                {FRESHNESS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                data-testid="documents-upload-btn"
                size="sm"
                onClick={() => {
                  // eslint-disable-next-line no-console
                  console.info(
                    "[DocumentsUpload] upload button clicked",
                    { studentId, currentDocCount: docs.length }
                  );
                  setUploadOpen(true);
                }}
                className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <UploadSimple size={12} className="mr-1" />
                Upload document
              </Button>
            </div>
          </div>

          {loading ? (
            <p className="text-[12.5px] text-[#6B7280] py-4 text-center">
              Loading documents…
            </p>
          ) : docs.length === 0 ? (
            <p
              className="text-[12.5px] text-[#6B7280] py-8 text-center"
              data-testid="documents-empty"
            >
              No supporting documents yet.
            </p>
          ) : (
            <ul className="divide-y divide-[#F1F2F4] border border-[#F1F2F4] rounded-lg">
              {docs.map((d) => (
                <DocumentRow
                  key={d.id}
                  doc={d}
                  studentId={studentId}
                  viewerIsAdmin={viewerIsAdmin}
                  onChanged={() => load({ background: true })}
                  onView={(toShow) => setViewingDoc(toShow)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {uploadOpen && (
        <UploadModal
          studentId={studentId}
          isAdmin={viewerIsAdmin}
          evidenceScope="general"
          onClose={() => setUploadOpen(false)}
          onUploaded={async (newDoc) => {
            // FP3-032C — Optimistic append so the new row appears
            // instantly without depending on the background refetch.
            // If the server already returned the serialized doc, drop
            // it onto the top of the list (newest first). The
            // subsequent refetch will normalize ordering.
            if (newDoc && newDoc.id) {
              setDocs((prev) => {
                if (prev.some((d) => d.id === newDoc.id)) return prev;
                return [newDoc, ...prev];
              });
            }
            setUploadOpen(false);
            // Background refresh keeps things consistent without
            // flashing the loading skeleton over the new row.
            await load({ background: true });
          }}
        />
      )}

      {/* FP3-032D — In-app viewer mounts only when a doc is selected.
          Closing or unmounting revokes the blob URL inside the modal. */}
      {viewingDoc && (
        <DocumentViewerModal
          studentId={studentId}
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────

// FP3-034B — `DocumentRow` is exported so the Assessments tab can
// render the SAME row UI (View / Download / Archive / Delete) for
// assessment-typed documents, without duplicating ~170 LOC.
export function DocumentRow({ doc, studentId, viewerIsAdmin, onChanged, onView }) {
  const [busy, setBusy] = useState(false);
  const [pendingArchive, setPendingArchive] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const freshnessTone =
    FRESHNESS_TONE[doc.evidence_freshness] || FRESHNESS_TONE.date_not_provided;
  const visibilityBadge =
    doc.visibility === "admin_only" ? (
      <span className="text-[10px] uppercase tracking-[0.08em] font-semibold rounded-full px-1.5 py-0.5 bg-[#F1F2F4] text-[#6B7280]">
        Admin only
      </span>
    ) : null;

  const download = async () => {
    try {
      const res = await api.downloadStudentDocument(studentId, doc.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name_original || doc.document_title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not download"
      );
    }
  };

  const confirmArchive = async () => {
    setBusy(true);
    try {
      await api.archiveStudentDocument(studentId, doc.id);
      toast.success("Document archived");
      setPendingArchive(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not archive");
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    setBusy(true);
    try {
      await api.deleteStudentDocument(studentId, doc.id);
      toast.success("Document deleted");
      setPendingDelete(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`document-row-${doc.id}`}
      className="px-4 py-3 hover:bg-[#FBFAF6]"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <FileText
              size={12}
              weight="duotone"
              className="text-[#3F4756] shrink-0"
            />
            <span
              data-testid={`document-title-${doc.id}`}
              className="text-[13px] font-medium text-[#1F2937] truncate"
            >
              {doc.document_title}
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border ${freshnessTone}`}
              data-testid={`document-freshness-${doc.id}`}
            >
              {FRESHNESS_LABEL[doc.evidence_freshness] || doc.evidence_freshness}
            </span>
            {visibilityBadge}
            {doc.status === "archived" && (
              <span className="text-[10px] uppercase tracking-[0.08em] font-semibold rounded-full px-1.5 py-0.5 bg-[#F1F2F4] text-[#6B7280]">
                Archived
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#6B7280] mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{TYPE_LABEL[doc.document_type] || doc.document_type}</span>
            {doc.document_date && (
              <>
                <span className="text-[#D6D3CB]">·</span>
                <Calendar size={9} weight="duotone" className="mr-0.5" />
                {doc.document_date}
              </>
            )}
            <span className="text-[#D6D3CB]">·</span>
            <span>
              Uploaded by {doc.uploaded_by_name}
              {doc.uploaded_by_role ? ` · ${doc.uploaded_by_role}` : ""}
            </span>
            <span className="text-[#D6D3CB]">·</span>
            <span>{fmtBytes(doc.file_size_bytes)}</span>
            <span className="text-[#D6D3CB]">·</span>
            <span className="font-mono">.{doc.file_extension}</span>
          </div>
          {(doc.service_area_tags?.length > 0 ||
            doc.domain_tags?.length > 0) && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap text-[10px]">
              <Tag size={9} weight="duotone" className="text-[#6B7280]" />
              {[...doc.service_area_tags, ...doc.domain_tags].map((t) => (
                <span
                  key={t}
                  className="rounded-full px-1.5 py-0.5 bg-[#F1F2F4] text-[#3F4756]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          {doc.summary_note && (
            <p className="text-[11.5px] text-[#3F4756] mt-2 whitespace-pre-wrap">
              {doc.summary_note}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* FP3-032D — View opens the in-app preview modal. PDFs and
              images render inline via authenticated blob URLs; TXT/CSV
              render as styled panels; everything else gets a calm
              "preview not available yet" fallback with Download. */}
          <button
            type="button"
            data-testid={`document-view-${doc.id}`}
            onClick={() => onView?.(doc)}
            className="text-[#3F4756] hover:text-[#1E3F33] p-1.5 rounded-md hover:bg-[#E8F0EB]"
            title="View"
            aria-label="View document"
          >
            <Eye size={13} />
          </button>
          <button
            type="button"
            data-testid={`document-download-${doc.id}`}
            onClick={download}
            className="text-[#3F4756] hover:text-[#1E3F33] p-1.5 rounded-md hover:bg-[#E8F0EB]"
            title="Download"
            aria-label="Download document"
          >
            <Download size={13} />
          </button>
          <button
            type="button"
            data-testid={`document-archive-${doc.id}`}
            onClick={() => setPendingArchive(true)}
            disabled={busy || doc.status === "archived"}
            className="text-[#6B7280] hover:text-[#7A5B1F] p-1.5 rounded-md hover:bg-[#FAF1DD] disabled:opacity-40"
            title="Archive"
            aria-label="Archive document"
          >
            <Archive size={13} />
          </button>
          <button
            type="button"
            data-testid={`document-delete-${doc.id}`}
            onClick={() => setPendingDelete(true)}
            disabled={busy}
            className="text-[#6B7280] hover:text-[#7A3A2C] p-1.5 rounded-md hover:bg-[#F8E8E2]"
            title="Delete"
            aria-label="Delete document"
          >
            <TrashSimple size={13} />
          </button>
        </div>
      </div>

      {/* FP3-032D — In-app confirmation dialogs replace the previous
          window.confirm popups for both archive and delete. */}
      <ConfirmDialog
        open={pendingArchive}
        onOpenChange={(o) => !busy && setPendingArchive(o)}
        title="Archive this document?"
        description="Archived documents stay on file but no longer show in the active list."
        confirmLabel="Archive"
        onConfirm={confirmArchive}
        testId={`document-archive-confirm-${doc.id}`}
      />
      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={(o) => !busy && setPendingDelete(o)}
        title="Delete document?"
        description="This document will no longer appear in the student's active documents. This cannot be undone."
        confirmLabel="Delete document"
        destructive
        onConfirm={confirmRemove}
        testId={`document-delete-confirm-${doc.id}`}
      />
    </li>
  );
}

// ── Upload modal ────────────────────────────────────────────────────

// FP3-034B — `UploadModal` is exported so the Assessments tab can
// reuse the exact same upload form (Field / file picker / FormData
// submit / [DocumentsUpload] instrumentation). Callers can pass
// `typeOptions` to restrict the document-type dropdown (e.g. the
// Assessments tab only offers assessment-flavored types).
export function UploadModal({
  studentId,
  isAdmin,
  onClose,
  onUploaded,
  typeOptions,
  defaultType,
  titleLabel,
  // FP3-034B correction — caller-declared scope so Documents tab
  // uploads stay in Documents and Assessments tab uploads stay in
  // Assessments unless intentionally moved.
  evidenceScope = "general",
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState(defaultType || "evaluation_report");
  const [docDate, setDocDate] = useState("");
  const [visibility, setVisibility] = useState("student_team");
  const [serviceTags, setServiceTags] = useState("");
  const [summary, setSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // FP3-032C — Inline visible error so providers don't need DevTools
  // to see why an upload failed (e.g. a brief toast can be missed,
  // especially if the screen reflows during the request).
  const [lastError, setLastError] = useState("");

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setLastError("");
    if (f && !title) {
      // Default title from filename (strip extension).
      setTitle(f.name.replace(/\.[^.]+$/, ""));
    }
    // eslint-disable-next-line no-console
    console.info("[DocumentsUpload] file selected", {
      file_name: f?.name,
      file_size: f?.size,
      file_type: f?.type || "(browser empty)",
    });
  };

  const submit = async () => {
    if (!file) {
      const msg = "Upload failed: file missing. Pick a file first.";
      setLastError(msg);
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    setLastError("");
    // FP3-032C — Capture which Student-Workspace tab is currently
    // active so we can verify after the upload completes that we
    // didn't accidentally bounce the user out of Documents.
    const tabBefore =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("tab") || "(none)"
        : "(unknown)";
    // eslint-disable-next-line no-console
    console.info("[DocumentsUpload] active tab before upload", {
      tab: tabBefore,
    });
    // eslint-disable-next-line no-console
    console.info("[DocumentsUpload] form submit fired", {
      studentId,
      file_name: file.name,
      file_size: file.size,
      file_type: file.type || "(browser empty)",
    });
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_title", title.trim() || file.name);
      form.append("document_type", docType);
      form.append("document_date", docDate);
      form.append("visibility", visibility);
      form.append("evidence_scope", evidenceScope);
      form.append("service_area_tags", serviceTags);
      form.append("summary_note", summary);
      // FP3-032B/C — Surface what the browser is about to send so a
      // network-capture screenshot is trivially reviewable.
      // eslint-disable-next-line no-console
      console.info("[DocumentsUpload] FormData keys", {
        keys: [...form.keys()],
      });
      // eslint-disable-next-line no-console
      console.info("[DocumentsUpload] upload request starting", {
        studentId,
        endpoint: `/api/students/${studentId}/documents`,
      });
      const res = await api.uploadStudentDocument(studentId, form);
      // eslint-disable-next-line no-console
      console.info("[DocumentsUpload] upload success", {
        status: res?.status,
        document_id: res?.data?.id,
      });
      // FP3-032C — Tab-state verification: confirm we are still on
      // Documents after the request returned. If this logs anything
      // else, a focus/visibility refetch is still tearing the tab
      // tree down somewhere.
      const tabAfter =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("tab") || "(none)"
          : "(unknown)";
      // eslint-disable-next-line no-console
      console.info("[DocumentsUpload] active tab after upload", {
        tab: tabAfter,
      });
      toast.success("Document uploaded");
      onUploaded?.(res.data);
    } catch (err) {
      // FP3-032B — Map status codes to calm, specific copy so a
      // confused provider has a clear next step instead of a
      // generic "upload failed" with no context.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("[DocumentsUpload] upload failure", {
        status,
        detail,
        file_name: file?.name,
        file_size: file?.size,
        file_type: file?.type,
      });
      let msg;
      if (typeof detail === "string" && detail) {
        msg = `Upload failed: ${detail}`;
      } else if (status === 401 || status === 403) {
        msg =
          "Upload failed: unauthorized. Sign in again and confirm you are on this student's team.";
      } else if (status === 400) {
        msg =
          "Upload failed: unsupported file type. Use PDF, DOC, DOCX, PNG, JPG, TXT, or CSV.";
      } else if (status === 404) {
        msg =
          "Upload failed: student not found in your scope.";
      } else if (status === 413) {
        msg =
          "Upload failed: file is larger than 25 MB.";
      } else if (status === 422) {
        msg = "Upload failed: required field missing.";
      } else if (status === 500 || status === 502 || status === 503) {
        msg =
          "Upload failed: server error. Please try again in a moment.";
      } else if (!err?.response) {
        msg =
          "Upload failed: network error. Check your connection and retry.";
      } else {
        msg =
          "Upload failed. Please try again or choose a supported file type.";
      }
      setLastError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // FP3-034E modal polish — hidden native file input + label-as-button
  // pattern. The shadcn DialogContent already renders its own close X
  // in the top-right, so we drop the duplicate header X. Picker is a
  // bordered drop-zone card with an icon, primary action button, and
  // selected-file readout below.
  const fileInputRef = useRef(null);
  const acceptList = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv";
  const acceptHelp = "PDF, DOC, DOCX, PNG, JPG, TXT, CSV · 25 MB max";

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="document-upload-modal"
        aria-describedby={undefined}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl shadow-xl"
      >
        {/* Header — pad the right side so the auto-rendered shadcn
            DialogClose X doesn't overlap the title text. */}
        <header className="px-6 pt-5 pb-4 border-b border-[#F1F2F4]">
          <DialogTitle
            className="pr-8 text-[15px] font-semibold text-[#1F2937] heading-font flex items-center gap-2 leading-tight"
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#E8F0EB] text-[#1E3F33] shrink-0">
              <UploadSimple size={15} weight="duotone" />
            </span>
            <span className="min-w-0 truncate">
              Upload supporting document
              {titleLabel ? (
                <span className="text-[#6B7280] font-medium"> — {titleLabel}</span>
              ) : null}
            </span>
          </DialogTitle>
          <p className="text-[11.5px] text-[#6B7280] mt-1.5 pl-9">
            Saved to {titleLabel === "Assessment" ? "Assessments" : "Documents"} for this student.
          </p>
        </header>

        <div className="px-6 py-5 space-y-4">
          <Field label="File" required>
            {/* Polished drop zone — hides the raw native control and
                surfaces a calm bordered card with an icon + button.
                Clicking anywhere on the zone triggers the picker. */}
            <input
              ref={fileInputRef}
              type="file"
              data-testid="document-upload-file"
              onChange={onFileChange}
              accept={acceptList}
              className="sr-only"
            />
            <button
              type="button"
              data-testid="document-upload-file-zone"
              onClick={() => fileInputRef.current?.click()}
              className={`w-full text-left flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all ${
                file
                  ? "border-[#1E3F33]/30 bg-[#F1F7F2] hover:bg-[#E8F0EB]"
                  : "border-dashed border-[#D6D2C4] bg-[#FBFAF6] hover:border-[#1E3F33]/40 hover:bg-[#F5F4EE]"
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
                  file
                    ? "bg-[#1E3F33] text-white"
                    : "bg-white text-[#1E3F33] border border-[#E5E3DB]"
                }`}
              >
                <FilePlus size={20} weight="duotone" />
              </span>
              <span className="flex-1 min-w-0">
                {file ? (
                  <>
                    <span
                      data-testid="document-upload-file-meta"
                      className="block text-[13px] font-medium text-[#1F2937] truncate"
                    >
                      {file.name}
                    </span>
                    <span className="block text-[11px] text-[#6B7280] mt-0.5">
                      {fmtBytes(file.size)}
                      {file.type ? ` · ${file.type}` : ""}
                      {" · "}
                      <span className="text-[#1E3F33] font-medium">Replace</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="block text-[13px] font-medium text-[#1F2937]">
                      Choose file
                    </span>
                    <span className="block text-[11px] text-[#6B7280] mt-0.5">
                      Click to browse · {acceptHelp}
                    </span>
                  </>
                )}
              </span>
            </button>
          </Field>

          {/* FP3-032C — Inline visible error so the reason a previous
              attempt failed stays on screen after the toast fades. */}
          {lastError && (
            <div
              data-testid="document-upload-error-banner"
              className="rounded-lg border border-[#E8C7BC] bg-[#F8E8E2] text-[#7A3A2C] text-[11.5px] px-3 py-2 flex items-start gap-2"
            >
              <Warning size={13} weight="duotone" className="mt-0.5 shrink-0" />
              <span>{lastError}</span>
            </div>
          )}

          <Field label="Title" required>
            <input
              type="text"
              data-testid="document-upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Spring 2026 Evaluation report"
              className={inputClasses}
            />
          </Field>

          <Field label="Document type" required>
            <select
              data-testid="document-upload-type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className={inputClasses}
            >
              {(typeOptions || DOCUMENT_TYPES).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Document date"
            hint="When was this document created or signed?"
          >
            <input
              type="date"
              data-testid="document-upload-date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              className={inputClasses}
            />
          </Field>

          <Field label="Visibility">
            <select
              data-testid="document-upload-visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className={inputClasses}
            >
              <option value="student_team">
                Student team (assigned providers + admin)
              </option>
              {isAdmin && (
                <option value="admin_only">Admin only</option>
              )}
            </select>
          </Field>

          <Field
            label="Service / domain tags"
            hint="Comma-separated. e.g. speech, articulation"
          >
            <input
              type="text"
              data-testid="document-upload-tags"
              value={serviceTags}
              onChange={(e) => setServiceTags(e.target.value)}
              placeholder="speech, language"
              className={inputClasses}
            />
          </Field>

          <Field
            label="Summary note"
            hint="Optional. Helps teammates understand the document."
          >
            <textarea
              data-testid="document-upload-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={2}
              maxLength={2000}
              className={`${inputClasses} resize-none`}
            />
          </Field>
        </div>

        <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-[#F1F2F4] bg-[#FBFAF6] rounded-b-2xl">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="document-upload-cancel"
            className="h-9 text-[12.5px] border-[#E5E3DB] text-[#4A4540] hover:bg-[#F1F2F4]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="document-upload-submit"
            size="sm"
            disabled={!file || !title.trim() || submitting}
            onClick={submit}
            className="h-9 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            <UploadSimple size={13} className="mr-1" />
            {submitting ? "Uploading…" : "Upload"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

// ── shared controls ────────────────────────────────────────────────

const inputClasses =
  "w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 bg-white focus:outline-none focus:border-[#1E3F33]";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold flex items-center gap-1">
        {label}
        {required && <span className="text-[#7A3A2C]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && (
        <span className="text-[10.5px] text-[#9AA0A6] mt-1 block">{hint}</span>
      )}
    </label>
  );
}
